import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { routerRequestAction } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { catchError, filter, map, mergeMap, of, takeUntil } from 'rxjs';
import {
  clearAllState,
  loadPageWithPatientIdParameter,
  startTourIfUnseen,
} from '../../../core/store/actions/app.actions';
import { TourType } from '../../../tours/data/tour-config';
import { patientSwitchInitiated } from '../../../user/store/actions/user.actions';
import { selectPatientId } from '../../../user/store/selectors/user.selectors';
import { PatientResultService } from '../../services/patient-result.service';
import {
  getPatientResults,
  getPatientResultsById,
  getPatientResultsByIdFailure,
  getPatientResultsByIdSuccess,
  getPatientResultsFailure,
  getPatientResultsSuccess,
  markAllAsReadFailure,
  markAllAsReadSuccess,
  nextPage,
  retryPage,
  rememberListPosition,
} from '../actions/patient-result.actions';
import {
  selectPageNumber,
  selectPageSize,
  selectPatientResultCount,
  selectPatientResults,
  selectPatientResultsFilters,
} from '../selectors/patient-result.selectors';

@Injectable()
export class PatientResultsEffects {
  private actions$ = inject(Actions);
  private patientResultService = inject(PatientResultService);
  private store = inject(Store);
  // Reads are stale the moment the user navigates away, so they also cancel on navigation.
  private readonly cancelRequest$ = this.actions$.pipe(
    ofType(patientSwitchInitiated, clearAllState, routerRequestAction)
  );
  // Writes (mark-as-read) must survive navigation; they cancel only when the patient changes
  // or the store is cleared (logout, session-clear, or account switch).
  private readonly cancelWrite$ = this.actions$.pipe(ofType(patientSwitchInitiated, clearAllState));

  getPatientResults$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getPatientResults, nextPage, retryPage),
      concatLatestFrom(() => [
        this.store.select(selectPatientId),
        this.store.select(selectPageNumber),
        this.store.select(selectPageSize),
        this.store.select(selectPatientResultCount),
        this.store.select(selectPatientResultsFilters),
      ]),
      mergeMap(([action, patientId, pageNumber, pageSize, existingCount, filters]) => {
        if (!patientId) {
          return of(getPatientResultsFailure({ error: 'Patient id is missing.' }));
        }

        const activeFilters = filters ?? {
          searchTerm: null,
          abnormalTypeId: null,
          labResultStatusTypeId: null,
          sortDirection: 2,
          sortColumn: 'observationDateUtc',
        };

        return this.patientResultService
          .getPatientResults(
            patientId,
            activeFilters.searchTerm,
            activeFilters.abnormalTypeId,
            activeFilters.labResultStatusTypeId,
            activeFilters.sortDirection,
            activeFilters.sortColumn,
            pageNumber ?? 1,
            pageSize ?? 15
          )
          .pipe(
            takeUntil(this.cancelRequest$),
            map(response => {
              let isAtEndOfData = false;
              const totalRecordCount = response.totalRecordCount;
              let currentPage = pageNumber || 1;
              let patientResults = response.result;
              const requestedPageSize = pageSize || 15;
              const alreadyLoadedCount = action.type === getPatientResults.type ? 0 : existingCount;

              if (patientResults == null || patientResults.length == 0) {
                patientResults = [];
                currentPage--;
                isAtEndOfData = true;
              }

              const loadedCount = alreadyLoadedCount + patientResults.length;
              const reachedTotal = totalRecordCount > 0 && loadedCount >= totalRecordCount;
              const shortPage = patientResults.length > 0 && patientResults.length < requestedPageSize;
              if (reachedTotal || shortPage) isAtEndOfData = true;

              return getPatientResultsSuccess({
                patientResults,
                pageNumber: currentPage <= 0 ? 1 : currentPage,
                isAtEndOfData,
                totalRecordCount,
              });
            }),
            catchError(err => {
              // Pass the HttpErrorResponse itself. Unwrapping to `err.message` degraded it to a plain
              // string, so classifyLoadError could never recognise a timeout or an offline failure and
              // every error rendered the generic server copy.
              return of(getPatientResultsFailure({ error: err }));
            })
          );
      })
    );
  });

  getPatientResultsById$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getPatientResultsById),
      concatLatestFrom(() => this.store.select(selectPatientId)),
      mergeMap(([action, patientId]) => {
        return this.patientResultService.getPatientResultsbyId(action.patientLabResultId, patientId).pipe(
          takeUntil(this.cancelRequest$),
          map(patientResult => {
            return getPatientResultsByIdSuccess({ patientResult });
          }),
          catchError(err => {
            return of(getPatientResultsByIdFailure({ error: err?.error?.errors ?? err?.message ?? err }));
          })
        );
      })
    );
  });

  getPatientResultsSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getPatientResultsSuccess),
      concatLatestFrom(() => [this.store.select(selectPatientResults), this.store.select(selectPatientId)]),
      filter(([, patientResults]) => patientResults.filter(result => !result.isRead).length > 0),
      mergeMap(([, patientResults, patientId]) => {
        const unreadPatientResultIds = patientResults
          .filter(result => !result.isRead)
          .map(result => result.patientLabResultId);
        return this.patientResultService.markAllAsRead(unreadPatientResultIds, patientId).pipe(
          takeUntil(this.cancelWrite$),
          map(() => {
            return markAllAsReadSuccess();
          }),
          catchError(err => {
            return of(markAllAsReadFailure({ error: err?.error?.errors ?? err?.message ?? err }));
          })
        );
      })
    );
  });

  startResultsTourIfUnseen$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getPatientResultsSuccess),
      concatLatestFrom(() => this.store.select(selectPatientResultCount)),
      //only start results tour on initial load when there are results to show
      filter(([action, count]) => action.pageNumber === 1 && count > 0),
      map(() => {
        return startTourIfUnseen({ tourType: TourType.Results });
      })
    );
  });

  // Opening a result records its list position (reducer) and navigates to the
  // detail. Navigation lives here so the component dispatches a single action.
  openResultDetail$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(rememberListPosition),
      map(({ lastViewedResultId }) => loadPageWithPatientIdParameter({ route: `results/${lastViewedResultId}` }))
    );
  });
}
